/**
 * Compact binary encoder / decoder for GPS flight tracks.
 *
 * Binary format v1 (little-endian):
 *   Header (5 bytes):
 *     [uint8  version = 1]
 *     [uint32 point_count]
 *   Per point (20 bytes): timestamp, lat, lon, alt
 *
 * Binary format v2 (adds g-force):
 *   Per point (22 bytes): same as v1 + [int16 gforce × 1000] (-32768 = no data)
 *
 * Size: 5 + (N × 20) bytes (v1) or 5 + (N × 22) bytes (v2).
 */

import type { FlightPoint } from '@/types/flight';

const FORMAT_VERSION_1 = 1;
const FORMAT_VERSION_2 = 2;
const HEADER_SIZE = 5; // 1 (version) + 4 (count)
const POINT_SIZE_V1 = 20; // 8 + 4 + 4 + 4
const POINT_SIZE_V2 = 22; // + 2 for gforce (int16 × 1000)
const COORD_SCALE = 1e7;
const ALT_SCALE = 10; // meters → decimeters
const GFORCE_SCALE = 1000;
const GFORCE_NONE = -32768; // int16 sentinel for "no g-force data"

/**
 * Encode an array of FlightPoints into compact binary format (v1, GPS only).
 */
export function encodeTrackBinary(points: FlightPoint[]): ArrayBuffer {
  const buffer = new ArrayBuffer(HEADER_SIZE + points.length * POINT_SIZE_V1);
  const view = new DataView(buffer);

  // Header
  view.setUint8(0, FORMAT_VERSION_1);
  view.setUint32(1, points.length, true);

  let offset = HEADER_SIZE;

  for (const point of points) {
    const ts = point.timestamp;
    const tsLow = ts & 0xffffffff;
    const tsHigh = Math.floor(ts / 0x100000000) & 0xffffffff;
    view.setUint32(offset, tsLow, true);
    view.setUint32(offset + 4, tsHigh, true);

    view.setInt32(offset + 8, Math.round(point.latitude * COORD_SCALE), true);
    view.setInt32(offset + 12, Math.round(point.longitude * COORD_SCALE), true);
    view.setInt32(offset + 16, Math.round((point.altitude ?? 0) * ALT_SCALE), true);

    offset += POINT_SIZE_V1;
  }

  return buffer;
}

/**
 * Decode compact binary format back into FlightPoint[].
 * Supports both v1 (no g-force) and v2 (with g-force) formats.
 */
export function decodeTrackBinary(buffer: ArrayBuffer): FlightPoint[] {
  const view = new DataView(buffer);

  if (buffer.byteLength < HEADER_SIZE) {
    throw new Error('Binary track data too short for header');
  }

  const version = view.getUint8(0);
  if (version !== FORMAT_VERSION_1 && version !== FORMAT_VERSION_2) {
    throw new Error(`Unsupported track format version: ${version}`);
  }

  const count = view.getUint32(1, true);
  const pointSize = version === FORMAT_VERSION_2 ? POINT_SIZE_V2 : POINT_SIZE_V1;
  const expectedSize = HEADER_SIZE + count * pointSize;

  if (buffer.byteLength < expectedSize) {
    throw new Error(
      `Binary track data too short: expected ${expectedSize} bytes, got ${buffer.byteLength}`,
    );
  }

  const points: FlightPoint[] = new Array(count);
  let offset = HEADER_SIZE;

  for (let i = 0; i < count; i++) {
    const tsLow = view.getUint32(offset, true);
    const tsHigh = view.getUint32(offset + 4, true);
    const timestamp = tsLow + tsHigh * 0x100000000;

    const latitude = view.getInt32(offset + 8, true) / COORD_SCALE;
    const longitude = view.getInt32(offset + 12, true) / COORD_SCALE;
    const altitude = view.getInt32(offset + 16, true) / ALT_SCALE;

    const point: FlightPoint = { timestamp, latitude, longitude, altitude };

    if (version === FORMAT_VERSION_2) {
      const gRaw = view.getInt16(offset + 20, true);
      if (gRaw !== GFORCE_NONE) {
        point.gforce = gRaw / GFORCE_SCALE;
      }
    }

    points[i] = point;
    offset += pointSize;
  }

  return points;
}

/**
 * Convert an ArrayBuffer to a base64 string.
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert a base64 string to an ArrayBuffer.
 */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Gzip-compress data using the CompressionStream API.
 * Falls back to uncompressed if CompressionStream is not available.
 */
export async function gzipCompress(data: ArrayBuffer): Promise<ArrayBuffer> {
  if (typeof CompressionStream === 'undefined') {
    // CompressionStream not available — return uncompressed
    return data;
  }

  const cs = new CompressionStream('gzip');
  const writer = cs.writable.getWriter();
  const reader = cs.readable.getReader();

  // Write the input data and close
  writer.write(new Uint8Array(data));
  writer.close();

  // Read all compressed chunks
  const chunks: Uint8Array[] = [];
  let totalLength = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    totalLength += value.byteLength;
  }

  // Concatenate chunks
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.byteLength;
  }

  return result.buffer;
}

/**
 * Gzip-decompress data using the DecompressionStream API.
 * Falls back to returning data as-is if DecompressionStream is not available.
 */
export async function gzipDecompress(data: ArrayBuffer): Promise<ArrayBuffer> {
  if (typeof DecompressionStream === 'undefined') {
    return data;
  }

  const ds = new DecompressionStream('gzip');
  const writer = ds.writable.getWriter();
  const reader = ds.readable.getReader();

  writer.write(new Uint8Array(data));
  writer.close();

  const chunks: Uint8Array[] = [];
  let totalLength = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    totalLength += value.byteLength;
  }

  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.byteLength;
  }

  return result.buffer;
}
